import { useState, useEffect, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import LinearProgress from "@mui/material/LinearProgress";
import Tooltip from "@mui/material/Tooltip";
import Chip from "@mui/material/Chip";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import AssignmentIcon from "@mui/icons-material/Assignment";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import FactCheckIcon from "@mui/icons-material/FactCheck";
import GavelIcon from "@mui/icons-material/Gavel";
import SendIcon from "@mui/icons-material/Send";
import PublishIcon from "@mui/icons-material/Publish";
import TimelineIcon from "@mui/icons-material/Timeline";
import NoteAltIcon from "@mui/icons-material/NoteAlt";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import VuiButton from "components/VuiButton";
import { useAuthz } from "context/AuthzContext";
import {
  getClientProfile,
  listClientJobs,
  createClientJob,
  listJobTasks,
  updateJobTask,
} from "services/tenantUserAdminService";
import { TC, FS, FW } from "../../tokens/text";

// ── Workflow definition ───────────────────────────────────────────────────────

const STEPS = [
  {
    id: "data_collection",
    icon: <UploadFileIcon sx={{ fontSize: 18 }} />,
    label: "Data Collection",
    subtitle: "Bank feeds, documents, trial balance",
    color: "#2196f3",
    items: [
      { id: "bank_feed",       label: "Connect bank feed / upload statements" },
      { id: "trial_balance",   label: "Import trial balance" },
      { id: "invoices",        label: "Upload sales & purchase invoices" },
      { id: "payroll",         label: "Obtain payroll summaries" },
      { id: "record_request",  label: "Send record request to client" },
      { id: "ocr_reviewed",    label: "Review OCR-extracted documents" },
    ],
  },
  {
    id: "transaction_review",
    icon: <AssignmentIcon sx={{ fontSize: 18 }} />,
    label: "Transaction Review",
    subtitle: "Categorisation, VAT, reconciliation",
    color: "#9c27b0",
    items: [
      { id: "categorise",       label: "Review auto-categorisation" },
      { id: "vat_check",        label: "Check VAT codes & rates" },
      { id: "missing_receipts", label: "Chase missing receipts" },
      { id: "duplicates",       label: "Remove duplicate transactions" },
      { id: "bank_recon",       label: "Complete bank reconciliation" },
    ],
  },
  {
    id: "adjustments",
    icon: <NoteAltIcon sx={{ fontSize: 18 }} />,
    label: "Adjustments & Journals",
    subtitle: "Depreciation, accruals, provisions",
    color: "#ff9800",
    items: [
      { id: "depreciation",  label: "Post depreciation journals" },
      { id: "accruals",      label: "Post accruals & prepayments" },
      { id: "payroll_jnl",   label: "Post payroll journals" },
      { id: "dl_adjust",     label: "Review director loan account" },
      { id: "ct_provision",  label: "Post corporation tax provision" },
    ],
  },
  {
    id: "financial_statements",
    icon: <AccountBalanceIcon sx={{ fontSize: 18 }} />,
    label: "Financial Statements",
    subtitle: "P&L, balance sheet, notes",
    color: "#4caf50",
    items: [
      { id: "pl",              label: "Review profit & loss account" },
      { id: "bs",              label: "Review balance sheet" },
      { id: "notes",           label: "Prepare notes to accounts" },
      { id: "share_capital",   label: "Confirm share capital" },
      { id: "psc",             label: "Verify PSC information" },
      { id: "related_parties", label: "Prepare related party disclosures" },
    ],
  },
  {
    id: "compliance_checks",
    icon: <FactCheckIcon sx={{ fontSize: 18 }} />,
    label: "Compliance Checks",
    subtitle: "Deadlines, disclosures, anomalies",
    color: "#00bcd4",
    items: [
      { id: "deadlines",        label: "Confirm filing deadlines" },
      { id: "disclosures",      label: "Check all required disclosures included" },
      { id: "bs_balance",       label: "Verify balance sheet balances" },
      { id: "directors_verify", label: "Verify director details with CH" },
      { id: "anomaly_review",   label: "Review AI-flagged anomalies" },
    ],
  },
  {
    id: "approval",
    icon: <GavelIcon sx={{ fontSize: 18 }} />,
    label: "Approval Workflow",
    subtitle: "Client review, e-signature",
    color: "#e91e63",
    items: [
      { id: "accountant_review", label: "Accountant manager review" },
      { id: "send_client",       label: "Send draft to client" },
      { id: "client_approved",   label: "Client approved accounts" },
      { id: "esignature",        label: "Obtain e-signature / letter of engagement" },
    ],
  },
  {
    id: "submission",
    icon: <PublishIcon sx={{ fontSize: 18 }} />,
    label: "Submission",
    subtitle: "Companies House & HMRC filing",
    color: "#0057d8",
    items: [
      { id: "ch_file",      label: "File accounts with Companies House" },
      { id: "hmrc_ct600",   label: "File CT600 with HMRC" },
      { id: "ch_receipt",   label: "Store Companies House filing receipt" },
      { id: "hmrc_receipt", label: "Store HMRC filing receipt" },
    ],
  },
  {
    id: "audit_trail",
    icon: <TimelineIcon sx={{ fontSize: 18 }} />,
    label: "Tracking & Audit",
    subtitle: "Status, history, audit log",
    color: "#607d8b",
    items: [
      { id: "status_updated",   label: "Update job status to Filed" },
      { id: "audit_log",        label: "Review audit trail of changes" },
      { id: "invoice_client",   label: "Invoice client" },
      { id: "close_job",        label: "Close & archive job" },
    ],
  },
];

// ── Helpers ───────────────────────────────────────────────────────────────────

function storageKey(tenantId, clientId) {
  return `apwf:${tenantId}:${clientId}`;
}

function loadState(tenantId, clientId) {
  try {
    const raw = localStorage.getItem(storageKey(tenantId, clientId));
    if (raw) return JSON.parse(raw);
  } catch (_) {}
  return null;
}

function saveState(tenantId, clientId, state) {
  try {
    localStorage.setItem(storageKey(tenantId, clientId), JSON.stringify(state));
  } catch (_) {}
}

function buildInitialState() {
  return STEPS.map((step) => ({
    stepId: step.id,
    items: step.items.reduce((acc, item) => {
      acc[item.id] = false;
      return acc;
    }, {}),
  }));
}

function getStepProgress(stepState) {
  const items = Object.values(stepState.items);
  if (items.length === 0) return 0;
  const done = items.filter(Boolean).length;
  return Math.round((done / items.length) * 100);
}

function getStepStatus(stepState) {
  const items = Object.values(stepState.items);
  const done = items.filter(Boolean).length;
  if (done === 0) return "not_started";
  if (done === items.length) return "complete";
  return "in_progress";
}

function formatDate(str) {
  if (!str) return "—";
  return new Date(str).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
}

// ── Sub-components ────────────────────────────────────────────────────────────

function StatusBadge({ status, color }) {
  const cfg = {
    not_started: { label: "Not Started", bg: "rgba(120,144,156,0.12)", border: "#90a4ae55", text: "#607d8b" },
    in_progress: { label: "In Progress", bg: "rgba(33,150,243,0.10)", border: "#2196f355", text: "#1565c0" },
    complete:    { label: "Complete",    bg: "rgba(76,175,80,0.12)",  border: "#4caf5055", text: "#2e7d32" },
  }[status] || { label: status, bg: "rgba(120,144,156,0.1)", border: "#ccc", text: "#607d8b" };

  return (
    <VuiBox
      px={1} py={0.2}
      sx={{ background: cfg.bg, border: `1px solid ${cfg.border}`, borderRadius: "6px", display: "inline-flex", alignItems: "center" }}
    >
      <VuiTypography sx={{ color: cfg.text, fontSize: FS.xs, fontWeight: FW.bold }}>{cfg.label}</VuiTypography>
    </VuiBox>
  );
}

function CheckItem({ label, checked, onChange }) {
  return (
    <VuiBox
      display="flex"
      alignItems="flex-start"
      gap={1.2}
      onClick={onChange}
      sx={{
        cursor: "pointer",
        py: 0.8,
        px: 1,
        borderRadius: "8px",
        transition: "background 0.15s",
        "&:hover": { background: "rgba(0,117,255,0.05)" },
      }}
    >
      <VuiBox
        sx={{
          mt: "2px",
          width: 18,
          height: 18,
          borderRadius: "5px",
          flexShrink: 0,
          border: checked ? "none" : "2px solid #b0c4d8",
          background: checked ? "#4caf50" : "transparent",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          transition: "all 0.15s",
        }}
      >
        {checked && <span style={{ color: "#fff", fontSize: "0.6rem" }}>✓</span>}
      </VuiBox>
      <VuiTypography
        variant="caption"
        sx={{
          color: checked ? TC.tertiary : TC.secondary,
          textDecoration: checked ? "line-through" : "none",
          fontSize: FS.md,
          lineHeight: 1.5,
          userSelect: "none",
        }}
      >
        {label}
      </VuiTypography>
    </VuiBox>
  );
}

function ProgressRing({ pct, color, size = 40 }) {
  const r = (size - 6) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: "rotate(-90deg)", flexShrink: 0 }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#e8f0fe" strokeWidth={5} />
      <circle
        cx={size / 2} cy={size / 2} r={r}
        fill="none"
        stroke={pct === 100 ? "#4caf50" : color}
        strokeWidth={5}
        strokeDasharray={circ}
        strokeDashoffset={offset}
        strokeLinecap="round"
        style={{ transition: "stroke-dashoffset 0.4s ease" }}
      />
    </svg>
  );
}

// ── Step panel ────────────────────────────────────────────────────────────────

function StepPanel({ step, stepState, onToggleItem, profile, tenantId, history }) {
  const progress = getStepProgress(stepState);

  const linkRow = (label, href, state = null) => (
    <VuiBox
      key={label}
      display="flex"
      alignItems="center"
      justifyContent="space-between"
      p={1.2}
      sx={{
        background: "rgba(0,87,216,0.04)",
        border: "1px solid rgba(0,87,216,0.12)",
        borderRadius: "8px",
        cursor: "pointer",
        "&:hover": { background: "rgba(0,87,216,0.09)" },
      }}
      onClick={() => href.startsWith("http")
        ? window.open(href, "_blank")
        : history.push(href, state || undefined)
      }
    >
      <VuiTypography sx={{ color: TC.link, fontSize: FS.sm }}>{label}</VuiTypography>
      <OpenInNewIcon sx={{ fontSize: 14, color: TC.link }} />
    </VuiBox>
  );

  const infoBox = (text, variant = "info") => {
    const cols = {
      info:    { bg: "rgba(33,150,243,0.07)",  border: "rgba(33,150,243,0.25)",  text: TC.link },
      warn:    { bg: "rgba(255,152,0,0.07)",   border: "rgba(255,152,0,0.28)",   text: "#bf360c" },
      success: { bg: "rgba(76,175,80,0.07)",   border: "rgba(76,175,80,0.28)",   text: "#2e7d32" },
    }[variant];
    return (
      <VuiBox p={1.5} sx={{ background: cols.bg, border: `1px solid ${cols.border}`, borderRadius: "8px", mb: 1 }}>
        <VuiTypography sx={{ color: cols.text, fontSize: FS.sm, lineHeight: 1.6 }}>{text}</VuiTypography>
      </VuiBox>
    );
  };

  const checklist = (
    <VuiBox mt={1.5} sx={{ background: "#fff", border: "1px solid #e4eef8", borderRadius: "10px", overflow: "hidden" }}>
      {step.items.map((item) => (
        <VuiBox key={item.id} sx={{ borderBottom: "1px solid #f0f4fa" }}>
          <CheckItem
            label={item.label}
            checked={!!stepState.items[item.id]}
            onChange={() => onToggleItem(item.id)}
          />
        </VuiBox>
      ))}
    </VuiBox>
  );

  const chData = profile?.companies_house_data || {};
  const nextDue = chData.next_accounts_due;
  const periodEnd = chData.next_accounts_period_end_on;

  // Step-specific content
  const stepContent = {
    data_collection: (
      <>
        {infoBox(
          `Collect all source documents for the period ending ${formatDate(periodEnd || null)}. ` +
          `Accounts are due ${formatDate(nextDue || null)}.`
        )}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📁 View & Upload Client Documents", `/client-profiles/${profile?.id}`, { tab: 8 })}
          {linkRow("🏦 Bank Feeds", "/bank-feeds")}
          {linkRow("📧 Send Record Request", `/client-profiles/${profile?.id}`, { tab: 4 })}
        </VuiBox>
        {checklist}
      </>
    ),
    transaction_review: (
      <>
        {infoBox("Review and categorise all transactions. Ensure VAT codes are correct and bank is fully reconciled.")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📊 Exceptions & Anomalies", "/exceptions")}
          {linkRow("🏦 Bank Reconciliation", "/bank-feeds")}
        </VuiBox>
        {checklist}
      </>
    ),
    adjustments: (
      <>
        {infoBox("Post all year-end journals before finalising the accounts.")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📝 Manual Adjustments", "/period-close")}
        </VuiBox>
        {checklist}
      </>
    ),
    financial_statements: (
      <>
        {infoBox(
          `Build and review the statutory financial statements for the period ending ${formatDate(periodEnd || null)}.`
        )}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📈 Financial Reports", "/reports")}
          {linkRow("🤝 Related Parties", `/client-profiles/${profile?.id}`)}
        </VuiBox>
        {checklist}
      </>
    ),
    compliance_checks: (
      <>
        {nextDue && (
          <VuiBox
            p={1.5} mb={1.5}
            sx={{ background: "rgba(244,67,54,0.06)", border: "1px solid rgba(244,67,54,0.22)", borderRadius: "8px" }}
          >
            <VuiTypography sx={{ color: "#c62828", fontSize: FS.sm, fontWeight: FW.bold }}>
              🗓 Filing deadline: {formatDate(nextDue)}
            </VuiTypography>
          </VuiBox>
        )}
        {infoBox("Run compliance checks before sending to client. Verify all disclosures are complete.")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("🏛 Companies House Register", `https://find-and-update.company-information.service.gov.uk/company/${profile?.companyRegNumber || ""}`)}
          {linkRow("🧑‍💼 Director Details", "/directors")}
        </VuiBox>
        {checklist}
      </>
    ),
    approval: (
      <>
        {infoBox("Obtain sign-off from both the responsible accountant and the client before filing.")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📧 Email Client", `/client-profiles/${profile?.id}`, { tab: 4 })}
          {linkRow("🔍 CS01 / Review", "/cs-review")}
        </VuiBox>
        {checklist}
      </>
    ),
    submission: (
      <>
        {infoBox("File accounts with Companies House and CT600 with HMRC. Store all receipts.", "success")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("🏛 File with Companies House", `/client-profiles/${profile?.id}`)}
          {linkRow("📋 File CT600 with HMRC", `/client-profiles/${profile?.id}`)}
        </VuiBox>
        {checklist}
      </>
    ),
    audit_trail: (
      <>
        {infoBox("Review the complete job timeline, update the status to Filed and archive.", "success")}
        <VuiBox mb={1.5} display="flex" flexDirection="column" gap={1}>
          {linkRow("📋 View Filing Tasks", `/tasks?clientId=${profile?.id}`)}
          {linkRow("🏢 Client Profile", `/client-profiles/${profile?.id}`)}
        </VuiBox>
        {checklist}
      </>
    ),
  };

  return (
    <VuiBox>
      {/* Step header */}
      <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={2.5} gap={2}>
        <VuiBox display="flex" alignItems="center" gap={1.5}>
          <VuiBox
            sx={{
              width: 42,
              height: 42,
              borderRadius: "12px",
              background: `${step.color}18`,
              border: `1px solid ${step.color}44`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: step.color,
            }}
          >
            {step.icon}
          </VuiBox>
          <VuiBox>
            <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.lg, lineHeight: 1.2 }}>
              {step.label}
            </VuiTypography>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.sm }}>{step.subtitle}</VuiTypography>
          </VuiBox>
        </VuiBox>
        <VuiBox display="flex" alignItems="center" gap={1.5} flexShrink={0}>
          <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, fontWeight: FW.medium }}>
            {progress}% done
          </VuiTypography>
          <ProgressRing pct={progress} color={step.color} />
        </VuiBox>
      </VuiBox>

      <Divider sx={{ mb: 2, borderColor: "#e8f0fe" }} />

      {/* Step body */}
      {stepContent[step.id] || checklist}
    </VuiBox>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AccountsPrepWorkflow() {
  const { profileId } = useParams();
  const history = useHistory();
  const { tenantId: authTenantId, isSuperAdmin } = useAuthz();
  const tenantId = authTenantId || "";

  const [profile,     setProfile]     = useState(null);
  const [loading,     setLoading]     = useState(true);
  const [activeStep,  setActiveStep]  = useState(0);
  const [stepStates,  setStepStates]  = useState(null);
  const [activeJob,   setActiveJob]   = useState(null);
  const [jobLoading,  setJobLoading]  = useState(false);

  // Load profile
  useEffect(() => {
    if (!tenantId || !profileId) return;
    setLoading(true);
    getClientProfile(tenantId, profileId)
      .then((data) => setProfile(data?.profile || data || null))
      .catch(() => setProfile(null))
      .finally(() => setLoading(false));
  }, [tenantId, profileId]);

  // Load/init workflow state from localStorage
  useEffect(() => {
    if (!tenantId || !profileId) return;
    const saved = loadState(tenantId, profileId);
    if (saved && saved.length === STEPS.length) {
      setStepStates(saved);
    } else {
      setStepStates(buildInitialState());
    }
  }, [tenantId, profileId]);

  // Load active accounts job
  useEffect(() => {
    if (!tenantId || !profileId) return;
    setJobLoading(true);
    listClientJobs(tenantId, profileId)
      .then((res) => {
        const jobs = res.jobs || [];
        const accountsJobs = jobs.filter(
          (j) => j.serviceId === "accounts" && j.status !== "cancelled" && j.status !== "filed" && j.status !== "invoiced"
        );
        // Use most recent active accounts job
        if (accountsJobs.length > 0) {
          accountsJobs.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
          setActiveJob(accountsJobs[0]);
        }
      })
      .catch(() => {})
      .finally(() => setJobLoading(false));
  }, [tenantId, profileId]);

  const handleToggleItem = useCallback((stepIndex, itemId) => {
    setStepStates((prev) => {
      const next = prev.map((s, i) => {
        if (i !== stepIndex) return s;
        return { ...s, items: { ...s.items, [itemId]: !s.items[itemId] } };
      });
      saveState(tenantId, profileId, next);
      return next;
    });
  }, [tenantId, profileId]);

  if (loading || !stepStates) {
    return (
      <DashboardLayout>
        <DashboardNavbar customTitle="Accounts Preparation" />
        <VuiBox display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </VuiBox>
        <Footer />
      </DashboardLayout>
    );
  }

  const clientName = profile?.legalName || profile?.legal_identity?.legal_name || profileId;
  const chData     = profile?.companies_house_data || {};
  const nextDue    = chData.next_accounts_due;
  const periodEnd  = chData.next_accounts_period_end_on;

  // Overall progress
  const totalItems = stepStates.reduce((n, s) => n + Object.keys(s.items).length, 0);
  const doneItems  = stepStates.reduce((n, s) => n + Object.values(s.items).filter(Boolean).length, 0);
  const overallPct = totalItems > 0 ? Math.round((doneItems / totalItems) * 100) : 0;

  const completedSteps = stepStates.filter((s) => getStepStatus(s) === "complete").length;

  return (
    <DashboardLayout>
      <DashboardNavbar customTitle={`Accounts Prep — ${clientName}`} />
      <VuiBox py={3}>

        {/* ── Back + Header ── */}
        <VuiBox display="flex" alignItems="center" gap={1.5} mb={3}>
          <Tooltip title="Back to client profile">
            <IconButton
              size="small"
              onClick={() => history.push(`/client-profiles/${profileId}`)}
              sx={{ color: TC.link, background: "rgba(0,87,216,0.07)", "&:hover": { background: "rgba(0,87,216,0.14)" }, borderRadius: "8px" }}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <VuiBox flex={1}>
            <VuiTypography sx={{ color: TC.primary, fontWeight: FW.bold, fontSize: FS.xl, lineHeight: 1.2 }}>
              📋 Accounts Preparation
            </VuiTypography>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.sm }}>
              {clientName}
              {periodEnd && ` · Year ended ${formatDate(periodEnd)}`}
              {nextDue && ` · Due ${formatDate(nextDue)}`}
            </VuiTypography>
          </VuiBox>

          {/* Overall progress pill */}
          <VuiBox
            px={2} py={1}
            sx={{ background: "rgba(0,87,216,0.07)", border: "1px solid rgba(0,87,216,0.18)", borderRadius: "12px", textAlign: "center", minWidth: 110 }}
          >
            <VuiTypography sx={{ color: TC.link, fontWeight: FW.bold, fontSize: FS.lg }}>{overallPct}%</VuiTypography>
            <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.xs }}>{completedSteps}/{STEPS.length} steps</VuiTypography>
          </VuiBox>
        </VuiBox>

        {/* ── Active job banner ── */}
        {!jobLoading && activeJob && (
          <VuiBox
            display="flex"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            p={1.5}
            mb={2.5}
            sx={{ background: "rgba(0,117,255,0.06)", border: "1px solid rgba(0,117,255,0.22)", borderRadius: "10px" }}
          >
            <VuiTypography sx={{ color: TC.link, fontSize: FS.sm }}>
              🗂 Active job: <strong>{activeJob.title}</strong>
              {activeJob.dueDate && ` · Due ${formatDate(activeJob.dueDate)}`}
            </VuiTypography>
            <VuiButton
              size="small"
              variant="outlined"
              color="info"
              onClick={() => history.push(`/client-profiles/${profileId}`)}
              sx={{ fontSize: FS.xs, py: 0.3, px: 1, borderColor: "rgba(0,117,255,0.4)", color: TC.link, whiteSpace: "nowrap" }}
            >
              View Job ↗
            </VuiButton>
          </VuiBox>
        )}

        {/* ── Overall progress bar ── */}
        <VuiBox mb={3} sx={{ background: "#fff", border: "1px solid #d8e6f7", borderRadius: "12px", p: 2 }}>
          <VuiBox display="flex" alignItems="center" justifyContent="space-between" mb={1}>
            <VuiTypography sx={{ color: TC.secondary, fontSize: FS.sm, fontWeight: FW.medium }}>Overall Progress</VuiTypography>
            <VuiTypography sx={{ color: overallPct === 100 ? TC.success : TC.link, fontSize: FS.sm, fontWeight: FW.bold }}>
              {doneItems} / {totalItems} tasks complete
            </VuiTypography>
          </VuiBox>
          <LinearProgress
            variant="determinate"
            value={overallPct}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: "#e8f0fe",
              "& .MuiLinearProgress-bar": {
                borderRadius: 4,
                background: overallPct === 100
                  ? "linear-gradient(90deg,#4caf50,#66bb6a)"
                  : "linear-gradient(90deg,#0057d8,#0075ff)",
              },
            }}
          />
          <VuiBox display="flex" justifyContent="space-between" mt={1}>
            {STEPS.map((step, i) => {
              const status = getStepStatus(stepStates[i]);
              return (
                <Tooltip key={step.id} title={`${step.label} — ${status.replace("_", " ")}`} arrow>
                  <VuiBox
                    onClick={() => setActiveStep(i)}
                    sx={{
                      width: 28,
                      height: 28,
                      borderRadius: "50%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      cursor: "pointer",
                      fontSize: "0.6rem",
                      fontWeight: FW.bold,
                      background:
                        status === "complete"    ? step.color :
                        status === "in_progress" ? `${step.color}33` :
                        "#e8f0fe",
                      color:
                        status === "complete"    ? "#fff" :
                        status === "in_progress" ? step.color :
                        "#b0c4d8",
                      border: i === activeStep ? `2px solid ${step.color}` : "2px solid transparent",
                      transition: "all 0.2s",
                      "&:hover": { transform: "scale(1.15)" },
                    }}
                  >
                    {status === "complete" ? "✓" : i + 1}
                  </VuiBox>
                </Tooltip>
              );
            })}
          </VuiBox>
        </VuiBox>

        {/* ── Main layout: Stepper + Panel ── */}
        <Grid container spacing={2.5} alignItems="flex-start">

          {/* ── Left: Vertical stepper ── */}
          <Grid item xs={12} md={3.5}>
            <VuiBox sx={{ background: "#fff", border: "1px solid #d8e6f7", borderRadius: "14px", overflow: "hidden" }}>
              {STEPS.map((step, i) => {
                const status  = getStepStatus(stepStates[i]);
                const pct     = getStepProgress(stepStates[i]);
                const isActive = i === activeStep;
                return (
                  <VuiBox
                    key={step.id}
                    onClick={() => setActiveStep(i)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      px: 2,
                      py: 1.5,
                      cursor: "pointer",
                      background: isActive ? `${step.color}0d` : "transparent",
                      borderLeft: isActive ? `3px solid ${step.color}` : "3px solid transparent",
                      borderBottom: i < STEPS.length - 1 ? "1px solid #f0f4fa" : "none",
                      transition: "all 0.15s",
                      "&:hover": {
                        background: isActive ? `${step.color}12` : "rgba(0,87,216,0.04)",
                      },
                    }}
                  >
                    {/* Step number / check */}
                    <VuiBox
                      sx={{
                        width: 30,
                        height: 30,
                        borderRadius: "8px",
                        flexShrink: 0,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        background: status === "complete" ? step.color : isActive ? `${step.color}20` : "#f0f4fa",
                        color: status === "complete" ? "#fff" : isActive ? step.color : "#b0c4d8",
                        fontSize: "0.7rem",
                        fontWeight: FW.bold,
                        transition: "all 0.15s",
                      }}
                    >
                      {status === "complete" ? "✓" : i + 1}
                    </VuiBox>

                    {/* Label + subtitle */}
                    <VuiBox flex={1} minWidth={0}>
                      <VuiTypography
                        sx={{
                          color: isActive ? TC.primary : status === "complete" ? TC.secondary : TC.secondary,
                          fontSize: FS.sm,
                          fontWeight: isActive ? FW.bold : FW.medium,
                          lineHeight: 1.3,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {step.label}
                      </VuiTypography>
                      <VuiTypography sx={{ color: TC.tertiary, fontSize: "0.65rem", lineHeight: 1.2 }}>
                        {pct}% · {status.replace("_", " ")}
                      </VuiTypography>
                    </VuiBox>

                    {/* Tiny progress ring */}
                    <ProgressRing pct={pct} color={step.color} size={28} />
                  </VuiBox>
                );
              })}
            </VuiBox>

            {/* Step nav buttons */}
            <VuiBox display="flex" gap={1} mt={2}>
              <VuiButton
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => setActiveStep((s) => Math.max(0, s - 1))}
                disabled={activeStep === 0}
                sx={{ flex: 1, fontSize: FS.xs, color: TC.secondary, borderColor: "#d8e6f7" }}
              >
                ← Prev
              </VuiButton>
              <VuiButton
                size="small"
                variant="contained"
                color="info"
                onClick={() => setActiveStep((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={activeStep === STEPS.length - 1}
                sx={{ flex: 1, fontSize: FS.xs, background: "#0057d8", "&:hover": { background: "#0041a8" } }}
              >
                Next →
              </VuiButton>
            </VuiBox>
          </Grid>

          {/* ── Right: Active step panel ── */}
          <Grid item xs={12} md={8.5}>
            <VuiBox sx={{ background: "#fff", border: "1px solid #d8e6f7", borderRadius: "14px", p: 3, minHeight: 480 }}>
              <StepPanel
                step={STEPS[activeStep]}
                stepState={stepStates[activeStep]}
                onToggleItem={(itemId) => handleToggleItem(activeStep, itemId)}
                profile={profile}
                tenantId={tenantId}
                history={history}
              />

              {/* Step actions */}
              <VuiBox mt={3} pt={2} display="flex" alignItems="center" justifyContent="space-between" sx={{ borderTop: "1px solid #f0f4fa" }}>
                <StatusBadge status={getStepStatus(stepStates[activeStep])} color={STEPS[activeStep].color} />
                <VuiBox display="flex" gap={1}>
                  {getStepProgress(stepStates[activeStep]) < 100 && (
                    <VuiButton
                      size="small"
                      variant="outlined"
                      color="info"
                      onClick={() => {
                        // Mark all items complete for this step
                        setStepStates((prev) => {
                          const next = prev.map((s, i) => {
                            if (i !== activeStep) return s;
                            const items = Object.keys(s.items).reduce((acc, k) => { acc[k] = true; return acc; }, {});
                            return { ...s, items };
                          });
                          saveState(tenantId, profileId, next);
                          return next;
                        });
                      }}
                      sx={{ fontSize: FS.xs, py: 0.4, borderColor: "rgba(0,117,255,0.4)", color: TC.link }}
                    >
                      ✓ Mark All Done
                    </VuiButton>
                  )}
                  {activeStep < STEPS.length - 1 && getStepProgress(stepStates[activeStep]) === 100 && (
                    <VuiButton
                      size="small"
                      variant="contained"
                      color="info"
                      onClick={() => setActiveStep((s) => s + 1)}
                      sx={{ fontSize: FS.xs, py: 0.4, background: "#0057d8", "&:hover": { background: "#0041a8" } }}
                    >
                      Continue to {STEPS[activeStep + 1]?.label} →
                    </VuiButton>
                  )}
                </VuiBox>
              </VuiBox>
            </VuiBox>

            {/* ── Client info card ── */}
            <VuiBox
              mt={2.5}
              p={2}
              sx={{ background: "#f8faff", border: "1px solid #d8e6f7", borderRadius: "12px", display: "flex", flexWrap: "wrap", gap: 2.5 }}
            >
              {[
                { label: "Company", value: clientName },
                { label: "Reg No.",  value: profile?.companyRegNumber || "—" },
                { label: "Year End", value: formatDate(periodEnd || null) },
                { label: "Accounts Due", value: formatDate(nextDue || null) },
                { label: "CT600 Due", value: chData.next_accounts_period_end_on
                    ? formatDate(new Date(new Date(chData.next_accounts_period_end_on).setFullYear(new Date(chData.next_accounts_period_end_on).getFullYear() + 1)).toISOString().slice(0,10))
                    : "—"
                },
              ].map(({ label, value }) => (
                <VuiBox key={label} minWidth={120}>
                  <VuiTypography sx={{ color: TC.tertiary, fontSize: FS.xs, textTransform: "uppercase", letterSpacing: "0.06em", display: "block" }}>
                    {label}
                  </VuiTypography>
                  <VuiTypography sx={{ color: TC.primary, fontSize: FS.sm, fontWeight: FW.medium }}>
                    {value}
                  </VuiTypography>
                </VuiBox>
              ))}
              <VuiBox display="flex" alignItems="flex-end" ml="auto">
                <VuiButton
                  size="small"
                  variant="outlined"
                  color="info"
                  onClick={() => history.push(`/client-profiles/${profileId}`)}
                  endIcon={<OpenInNewIcon sx={{ fontSize: "13px !important" }} />}
                  sx={{ fontSize: FS.xs, py: 0.4, borderColor: "rgba(0,117,255,0.4)", color: TC.link }}
                >
                  Client Profile
                </VuiButton>
              </VuiBox>
            </VuiBox>
          </Grid>
        </Grid>
      </VuiBox>
      <Footer />
    </DashboardLayout>
  );
}

